import { can, type OrganisationRole } from '@evw/domain';
import { Button, Card } from '@evw/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { archiveCoreRecord, createStaff, listStaff, updateStaff } from '../tenancy/api';

export function StaffPage({
  organisationId,
  role
}: {
  organisationId: string;
  role: OrganisationRole;
}) {
  const queryClient = useQueryClient();
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [email, setEmail] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['staff', organisationId],
    queryFn: () => listStaff(organisationId)
  });
  const save = useMutation({
    mutationFn: () => {
      const input = {
        given_name: givenName.trim(),
        family_name: familyName.trim(),
        display_name: `${givenName} ${familyName}`.trim(),
        email: email.trim()
      };
      return editingId
        ? updateStaff(organisationId, editingId, input)
        : createStaff(organisationId, input);
    },
    onSuccess: async () => {
      setGivenName('');
      setFamilyName('');
      setEmail('');
      setEditingId(null);
      await queryClient.invalidateQueries({ queryKey: ['staff', organisationId] });
    }
  });
  const archive = useMutation({
    mutationFn: (id: string) => archiveCoreRecord('staff', organisationId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff', organisationId] })
  });
  const editable = can(role, 'create', 'staff');
  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }
  return (
    <div className="stack">
      <Card>
        <h2>Staff</h2>
        <p className="muted">People who provide or support services.</p>
        {editable ? (
          <form className="inline-form" onSubmit={submit}>
            <label>
              Given name
              <input
                required
                maxLength={80}
                value={givenName}
                onChange={(e) => setGivenName(e.target.value)}
              />
            </label>
            <label>
              Family name
              <input
                required
                maxLength={80}
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
              />
            </label>
            <label>
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <div className="form-actions">
              <Button disabled={save.isPending}>
                {editingId ? 'Save staff member' : 'Add staff member'}
              </Button>
              {editingId ? (
                <Button
                  className="button-secondary"
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setGivenName('');
                    setFamilyName('');
                    setEmail('');
                  }}
                >
                  Cancel
                </Button>
              ) : null}
            </div>
          </form>
        ) : null}
        {save.error ? <p className="error">{save.error.message}</p> : null}
      </Card>
      <Card>
        <div className="records">
          {query.isLoading ? (
            <p>Loading…</p>
          ) : query.error ? (
            <p className="error">{query.error.message}</p>
          ) : query.data?.length ? (
            query.data.map((person) => (
              <article className="record" key={person.id}>
                <div>
                  <strong>{person.display_name}</strong>
                  <p>{person.email || 'No email recorded'}</p>
                </div>
                {editable ? (
                  <div className="record-actions">
                    <Button
                      className="button-secondary"
                      onClick={() => {
                        setEditingId(person.id);
                        setGivenName(person.given_name);
                        setFamilyName(person.family_name);
                        setEmail(person.email ?? '');
                      }}
                    >
                      Edit
                    </Button>
                    <Button className="button-secondary" onClick={() => archive.mutate(person.id)}>
                      Archive
                    </Button>
                  </div>
                ) : null}
              </article>
            ))
          ) : (
            <p className="muted">No staff yet.</p>
          )}
        </div>
      </Card>
    </div>
  );
}
